import React, { useState, useContext, useCallback, useEffect, useMemo } from 'react'
import styles from './MediaView.module.scss'
import MediaCorner from './MediaCorner'
import SidecarVersions from './SidecarVersions'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import {
  LIST_SHOOTS_QUERY,
  GET_SHOOT_QUERY,
  GET_SHOOT_DOWNLOAD_QUERY,
  CREATE_SHOOT_MUTATION,
  ADD_ASSETS_TO_SHOOT_MUTATION,
  REMOVE_ASSET_FROM_SHOOT_MUTATION,
  CHECK_OUT_SHOOT_MUTATION,
  CHECK_IN_SHOOT_MUTATION,
  DELETE_SHOOT_MUTATION,
  UPDATE_SHOOT_DATE_MUTATION,
  SET_SHOOT_VISIBILITY_MUTATION,
  GET_PROJECTS_QUERY,
  GET_TASKS_BY_PROJECT_QUERY,
  ATTACH_TASK_OUTPUTS_MUTATION,
  DETACH_TASK_OUTPUT_MUTATION,
  GET_TASKS_FOR_MEDIA_PATHS_QUERY,
  MY_ACCESS_QUERY
} from '../query/gqlQueries'
import UserContext from '../context/UserContext'

import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import LockIcon from '@mui/icons-material/Lock'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import DownloadIcon from '@mui/icons-material/Download'

import MediaLibrary from './MediaLibrary'
import MediaDeliveries from './MediaDeliveries'
import ClientPortal from './ClientPortal'

// File classification for the asset grid.
const VIDEO_EXTS = new Set(['.mp4', '.mov', '.m4v', '.avi', '.mkv', '.mxf'])
const extOf = (name) => { const d = String(name || '').lastIndexOf('.'); return d > 0 ? name.slice(d).toLowerCase() : '' }
const isVideoName = (name) => VIDEO_EXTS.has(extOf(name))

// Human-friendly byte formatter: 1234567890 → "1.15 GB"
function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 2)} ${units[i]}`
}

// Group a flat list of shoots by their parent project title, preserving the
// server's ordering (project ASC, createdAt DESC).
function groupShootsByProject(shoots) {
  const groups = []
  const index = {}
  for (const shoot of shoots) {
    const project = shoot.properties.project || 'Ungrouped'
    if (!(project in index)) {
      index[project] = groups.length
      groups.push({ project, shoots: [] })
    }
    groups[index[project]].shoots.push(shoot)
  }
  return groups
}

// The GraphQL server composes and SIGNS every media URL using ITS
// IMAGE_STORE_PUBLIC_URL (which may be a stale 'localhost:4001'). Rewrite only
// the origin to whichever image host this client can actually reach. Path AND
// query are preserved — the signature lives in the query string, so dropping
// it would make every URL 403.
const IMAGE_STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI
function storeUrl(url) {
  if (!url) return url
  if (!IMAGE_STORE_URI) return url
  try {
    const u = new URL(url)
    return IMAGE_STORE_URI.replace(/\/$/, '') + u.pathname + u.search
  } catch {
    return url
  }
}

function todayISO() {
  return new Date().toISOString().split('T')[0]
}

// A YYYY-MM-DD (or ISO/Neo4j) shoot date → "11 Mar 2026". Empty → null.
function formatShootDate(val) {
  if (!val) return null
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [y, m, d] = val.split('-').map(Number)
      return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    }
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch { return null }
}

// "2h ago", "yesterday", "Mar 11", "2024-03-11"
function formatRelative(iso) {
  if (!iso) return null
  let d
  try { d = new Date(iso) } catch (e) { return null }
  if (isNaN(d.getTime())) return null
  const s = Math.floor((Date.now() - d.getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  if (s < 86400 * 2) return 'yesterday'
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' })
}

export default function MediaView({ setIsVisible }) {
  const { username } = useContext(UserContext)
  // 'shoots' | 'library' | 'picking' | 'deliveries'. 'picking' is the Library
  // opened in selection mode to add originals to the shoot currently open.
  const [mode, setMode] = useState('shoots')
  const [selectedId, setSelectedId] = useState(null)
  // Lightbox: index into the open shoot's assets, or null when closed.
  const [previewIdx, setPreviewIdx] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newProject, setNewProject] = useState('')
  const [newNotes, setNewNotes] = useState('')
  const [newShootDate, setNewShootDate] = useState(todayISO())
  const [newPrivate, setNewPrivate] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [editingDate, setEditingDate] = useState(false)
  const [localDate, setLocalDate] = useState('')
  const [downloadHint, setDownloadHint] = useState(null)

  // Which kind of account this is. A client sees the portal instead of the
  // internal views — the server refuses them either way, but showing an
  // internal UI that errors on every query would be a poor way to find out.
  const { data: accessData, loading: accessLoading } = useQuery(MY_ACCESS_QUERY, {
    skip: !username,
    fetchPolicy: 'cache-and-network',
  })
  const role = accessData?.myAccess?.role

  // Existing projects — a shoot must belong to one that already exists.
  const { data: projectsData } = useQuery(GET_PROJECTS_QUERY, {
    variables: { username },
    skip: !username
  })
  const existingProjects = projectsData?.getProjects || []

  const { data: listData, loading: listLoading, refetch: refetchList } = useQuery(
    LIST_SHOOTS_QUERY,
    { variables: { project: null }, skip: !username, fetchPolicy: 'cache-and-network' }
  )

  const [fetchDetail, { data: detailData, loading: detailLoading, refetch: refetchDetail }] =
    useLazyQuery(GET_SHOOT_QUERY, { fetchPolicy: 'cache-and-network' })
  const [fetchShootDownload] = useLazyQuery(GET_SHOOT_DOWNLOAD_QUERY, { fetchPolicy: 'network-only' })

  // Task-output linking: which tasks a file was produced for, and the picker.
  const [fetchProjectTasks, { data: projTasksData }] = useLazyQuery(GET_TASKS_BY_PROJECT_QUERY, { fetchPolicy: 'cache-and-network' })
  const [fetchPathTasks, { data: pathTasksData }] = useLazyQuery(GET_TASKS_FOR_MEDIA_PATHS_QUERY, { fetchPolicy: 'network-only' })
  const [attachTaskOutputs] = useMutation(ATTACH_TASK_OUTPUTS_MUTATION)
  const [detachTaskOutput] = useMutation(DETACH_TASK_OUTPUT_MUTATION)
  const [taskPickerOpen, setTaskPickerOpen] = useState(false)
  const [taskQuery, setTaskQuery] = useState('')

  const [createShoot, { loading: creating }] = useMutation(CREATE_SHOOT_MUTATION)
  const [addAssetsToShoot] = useMutation(ADD_ASSETS_TO_SHOOT_MUTATION)
  const [removeAssetFromShoot] = useMutation(REMOVE_ASSET_FROM_SHOOT_MUTATION)
  const [checkOutShoot] = useMutation(CHECK_OUT_SHOOT_MUTATION)
  const [checkInShoot] = useMutation(CHECK_IN_SHOOT_MUTATION)
  const [deleteShoot] = useMutation(DELETE_SHOOT_MUTATION)
  const [updateShootDate] = useMutation(UPDATE_SHOOT_DATE_MUTATION)
  const [setShootVisibility] = useMutation(SET_SHOOT_VISIBILITY_MUTATION)

  const shoots = listData?.listShoots || []
  const shoot = detailData?.getShoot?.shoot
  const assets = useMemo(() => detailData?.getShoot?.assets || [], [detailData])

  // The server pairs sidecars to the file they describe and nests them, so
  // this list is already one card per shot. It used to be recomputed here by
  // string prefix on every render — inconsistent between views, and lost the
  // moment a file moved.
  const items = assets

  const shootProject = shoot?.properties?.project || null

  useEffect(() => {
    if (shootProject) fetchProjectTasks({ variables: { projectName: shootProject } })
  }, [shootProject, fetchProjectTasks])

  useEffect(() => {
    const paths = items.map((i) => i.path).filter(Boolean)
    if (paths.length) fetchPathTasks({ variables: { paths } })
  }, [items, fetchPathTasks])

  const projectTasks = (projTasksData?.getTasksByProject || []).map((r) => r.task).filter(Boolean)
  const pathTaskMap = useMemo(() => {
    const m = {}
    for (const row of pathTasksData?.getTasksForMediaPaths || []) m[row.path] = row.tasks || []
    return m
  }, [pathTasksData])

  const [linkMsg, setLinkMsg] = useState(null)
  const handleLinkToTask = useCallback(async (asset, taskId) => {
    if (!asset?.path || !taskId) return
    try {
      await attachTaskOutputs({ variables: { taskId, paths: [asset.path], username } })
      await fetchPathTasks({ variables: { paths: items.map((i) => i.path).filter(Boolean) } })
      setLinkMsg('Linked to task ✓')
      setTimeout(() => setLinkMsg(null), 2000)
    } catch (err) {
      window.alert(err.message || 'Could not link to task')
    }
  }, [attachTaskOutputs, username, items, fetchPathTasks])

  const handleUnlinkTask = useCallback(async (asset, taskId) => {
    if (!asset?.path || !taskId) return
    try {
      await detachTaskOutput({ variables: { taskId, path: asset.path } })
      await fetchPathTasks({ variables: { paths: items.map((i) => i.path).filter(Boolean) } })
    } catch (err) {
      window.alert(err.message || 'Could not unlink')
    }
  }, [detachTaskOutput, items, fetchPathTasks])

  useEffect(() => { setTaskPickerOpen(false); setTaskQuery('') }, [previewIdx])

  // Lightbox keyboard nav: ← → to move, Esc to close.
  useEffect(() => {
    if (previewIdx === null) return
    const onKey = (e) => {
      if (e.key === 'Escape') setPreviewIdx(null)
      else if (e.key === 'ArrowRight') setPreviewIdx((i) => (i === null ? i : Math.min(i + 1, items.length - 1)))
      else if (e.key === 'ArrowLeft') setPreviewIdx((i) => (i === null ? i : Math.max(i - 1, 0)))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [previewIdx, items.length])

  const handleSelect = useCallback((id) => {
    setSelectedId(id)
    setPreviewIdx(null)
    fetchDetail({ variables: { identity: id } })
  }, [fetchDetail])

  const handleBack = () => {
    setSelectedId(null)
    setIsCreating(false)
    setCreateError(null)
    setNewName('')
    setNewProject('')
    setNewNotes('')
  }

  // Originals picked in the Library are grouped under this shoot by edge —
  // nothing is copied and the files never leave _library/.
  const handleAddAssets = useCallback(async (paths) => {
    if (!selectedId || !paths?.length) return
    try {
      await addAssetsToShoot({ variables: { identity: selectedId, paths } })
      setMode('shoots')
      await refetchDetail({ identity: selectedId })
      await refetchList()
    } catch (err) {
      window.alert(err.message || 'Could not add these to the shoot')
    }
  }, [selectedId, addAssetsToShoot, refetchDetail, refetchList])

  const handleRemoveAsset = useCallback(async (asset) => {
    if (!selectedId || !asset?.path) return
    if (!window.confirm(
      `Remove "${asset.filename}" from this shoot?\n\n` +
      'The file itself stays in the library — only the grouping is removed.'
    )) return
    try {
      await removeAssetFromShoot({ variables: { identity: selectedId, path: asset.path } })
      setPreviewIdx(null)
      await refetchDetail({ identity: selectedId })
      await refetchList()
    } catch (err) {
      window.alert(err.message || 'Could not remove')
    }
  }, [selectedId, removeAssetFromShoot, refetchDetail, refetchList])

  // Pull a whole shoot to the editor's own drive.
  //
  // A manifest rather than a browser download: a shoot is routinely tens of
  // gigabytes, browsers refuse to start hundreds of downloads, and a
  // server-built zip would double the mini's disk traffic and fail as a single
  // unit. curl ships with macOS, downloads in parallel, resumes, and skips
  // what is already there — so nothing has to be installed.
  const [downloading, setDownloading] = useState(false)
  const handleDownloadShoot = useCallback(async () => {
    if (!selectedId) return
    setDownloading(true)
    try {
      const { data } = await fetchShootDownload({ variables: { identity: selectedId } })
      const dl = data?.getShootDownload
      if (!dl || !dl.files.length) { window.alert('Nothing in this shoot to download.'); return }

      const gb = (dl.totalBytes / 1073741824).toFixed(2)
      const lines = [
        `# ${dl.shoot} — ${dl.fileCount} files, ${gb} GB`,
        `# Links expire ${new Date(dl.expiresAt).toLocaleString()}.`,
        '#',
        '# Save this file, then in the folder you want the media in, run:',
        '#',
        '#     curl --parallel --parallel-max 4 -C - --create-dirs -K THIS_FILE',
        '#',
        '# -C - resumes a part-finished transfer, so re-running it after an',
        '# interruption picks up where it stopped rather than starting over.',
        '',
      ]
      for (const f of dl.files) {
        // Rewrite the origin the same way thumbnails are, so the manifest
        // points at whichever image host this client can actually reach.
        lines.push(`url = "${storeUrl(f.url)}"`)
        lines.push(`output = "${f.output}"`)
        lines.push('')
      }

      const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${(dl.shoot || 'shoot').replace(/[^a-zA-Z0-9-_]+/g, '-').toLowerCase()}.curl`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(a.href), 10000)
      setDownloadHint({ file: a.download, count: dl.fileCount, gb })
    } catch (err) {
      window.alert(err.message || 'Could not prepare the download')
    } finally {
      setDownloading(false)
    }
  }, [selectedId, fetchShootDownload])

  const handleCreate = useCallback(async (e) => {
    e?.preventDefault?.()
    if (!newName.trim() || !newProject || !username) return
    setCreateError(null)
    try {
      const { data } = await createShoot({
        variables: {
          input: {
            name: newName.trim(),
            project: newProject,
            notes: newNotes.trim() || null,
            shootDate: newShootDate || null,
            visibility: newPrivate ? 'private' : 'internal'
          }
        }
      })
      const created = data?.createShoot
      setIsCreating(false)
      setNewName(''); setNewProject(''); setNewNotes(''); setNewPrivate(false)
      setNewShootDate(todayISO())
      await refetchList()
      if (created?.identity) handleSelect(created.identity)
    } catch (err) {
      setCreateError(err.message || 'Could not create shoot.')
    }
  }, [newName, newProject, newNotes, newShootDate, newPrivate, username, createShoot, refetchList, handleSelect])

  const handleSaveDate = useCallback(async (id) => {
    if (!localDate) { setEditingDate(false); return }
    try {
      await updateShootDate({ variables: { identity: id, shootDate: localDate } })
      setEditingDate(false)
      await refetchDetail({ identity: id })
      await refetchList()
    } catch (err) { console.warn('updateShootDate failed', err) }
  }, [localDate, updateShootDate, refetchDetail, refetchList])

  const handleToggleVisibility = useCallback(async (id, current) => {
    const next = current === 'private' ? 'internal' : 'private'
    if (next === 'private' && !window.confirm(
      'Make this shoot private?\n\n' +
      'It becomes invisible to everyone except you — it disappears from listings ' +
      'for the rest of the team, rather than just being locked.'
    )) return
    try {
      await setShootVisibility({ variables: { identity: id, visibility: next } })
      await refetchDetail({ identity: id })
      await refetchList()
    } catch (err) {
      window.alert(err.message || 'Could not change visibility')
    }
  }, [setShootVisibility, refetchDetail, refetchList])

  const handleCheckOut = useCallback(async (id) => {
    try {
      await checkOutShoot({ variables: { identity: id } })
      await refetchDetail({ identity: id }); await refetchList()
    } catch (err) { console.warn('checkOut failed', err) }
  }, [checkOutShoot, refetchDetail, refetchList])

  const handleCheckIn = useCallback(async (id) => {
    try {
      await checkInShoot({ variables: { identity: id } })
      await refetchDetail({ identity: id }); await refetchList()
    } catch (err) { console.warn('checkIn failed', err) }
  }, [checkInShoot, refetchDetail, refetchList])

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm(
      'Delete this shoot?\n\n' +
      'The originals are NOT touched — they stay in the library and in any other ' +
      'shoot or project that uses them. Only this grouping is removed.'
    )) return
    try {
      await deleteShoot({ variables: { identity: id } })
      setSelectedId(null)
      await refetchList()
    } catch (err) { console.warn('delete failed', err) }
  }, [deleteShoot, refetchList])

  if (!username) {
    return (
      <div className={styles.view}>
        <MediaCorner onClose={() => setIsVisible(false)} />
        <div className={styles.status}>Sign in to view shoots.</div>
      </div>
    )
  }

  // A client account never reaches the internal views. Held until the role
  // is known, so an internal view never flashes up before redirecting.
  if (accessLoading && !role) {
    return (
      <div className={styles.view}>
        <MediaCorner onClose={() => setIsVisible(false)} />
        <div className={styles.status}>Loading…</div>
      </div>
    )
  }

  // Anything that is not team or owner gets the portal. Matching on 'client'
  // alone would drop every other role — a language-app account, a new role, a
  // typo — into the internal view, where every query errors.
  if (role && role !== 'team' && role !== 'owner') {
    return <ClientPortal setIsVisible={setIsVisible} />
  }

  // ───────────────────── DELIVERIES ─────────────────────
  if (mode === 'deliveries') {
    return (
      <MediaDeliveries
        username={username}
        onBack={() => setMode('shoots')}
        setIsVisible={setIsVisible}
      />
    )
  }

  // ───────────────────── LIBRARY ─────────────────────
  // One component, two modes: browsing the originals, or picking some to group
  // under the shoot that's open.
  if (mode === 'library' || mode === 'picking') {
    return (
      <MediaLibrary
        username={username}
        onBack={() => setMode('shoots')}
        setIsVisible={setIsVisible}
        pickMode={mode === 'picking'}
        pickLabel={shoot ? shoot.properties.name : ''}
        onPick={handleAddAssets}
      />
    )
  }

  // ───────────────────── SHOOT DETAIL ─────────────────────
  if (selectedId && shoot) {
    const p = shoot.properties
    const isCheckedOut = !!p.checkedOutBy
    const byMe = p.checkedOutBy === username
    const isPrivate = p.visibility === 'private'
    const mine = p.username === username
    const totalBytes = assets.reduce((sum, a) => sum + (a.size || 0), 0)

    return (
      <div className={styles.view}>
        <div className={styles.gridBg} />
        {previewIdx === null && (
          <MediaCorner onClose={() => setIsVisible(false)} />
        )}

        <div className={styles.container}>
          <header className={styles.header}>
            <button className={styles.backBtn} onClick={handleBack}>
              <ArrowBackIcon fontSize="small" /> Shoots
            </button>
            <div className={styles.titleGroup}>
              <div className={styles.label}>{p.project || 'Media'}</div>
              <h1 className={styles.title}>{p.name}</h1>
              {p.notes && <p className={styles.notes}>{p.notes}</p>}
            </div>
            <div className={styles.toolbar}>
              <button
                className={`${styles.iconBtn} ${styles.primaryBtn}`}
                onClick={() => setMode('picking')}
                title="Pick originals from the library to group under this shoot">
                <AddIcon fontSize="small" /> Add from Library
              </button>
              <button
                className={styles.iconBtn}
                title="Pull every original in this shoot to your own drive"
                disabled={downloading || assets.length === 0}
                onClick={handleDownloadShoot}>
                <DownloadIcon fontSize="small" /> {downloading ? 'Preparing…' : 'Pull to drive'}
              </button>
              <button className={styles.iconBtn} title="Reload" onClick={() => refetchDetail({ identity: selectedId })}>
                <RefreshIcon fontSize="small" />
              </button>
              <button
                className={styles.iconBtn}
                title={mine
                  ? (isPrivate ? 'Private — only you can see this. Click to share with the team.' : 'Shared with the team. Click to make private.')
                  : 'Only the shoot’s creator can change visibility'}
                disabled={!mine}
                onClick={() => handleToggleVisibility(selectedId, p.visibility)}>
                {isPrivate ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </button>
              <button className={`${styles.iconBtn} ${styles.dangerBtn}`} title="Delete shoot" onClick={() => handleDelete(selectedId)}>
                <DeleteOutlineIcon fontSize="small" />
              </button>
            </div>
          </header>

          <div className={styles.detailMeta}>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Contents</span>
              <span className={styles.metaValue}>
                {assets.length.toLocaleString()} original{assets.length === 1 ? '' : 's'} · {formatBytes(totalBytes)}
              </span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Shoot date</span>
              {editingDate ? (
                <span className={styles.metaValue} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="date"
                    className={styles.input}
                    style={{ padding: '0.2rem 0.4rem', width: 'auto' }}
                    value={localDate}
                    onChange={e => setLocalDate(e.target.value)}
                    autoFocus
                  />
                  <button className={styles.iconBtn} onClick={() => handleSaveDate(selectedId)} title="Save">✓</button>
                  <button className={styles.iconBtn} onClick={() => setEditingDate(false)} title="Cancel">✕</button>
                </span>
              ) : (
                <span
                  className={styles.metaValue}
                  style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}
                  onClick={() => { setLocalDate(p.shootDate || todayISO()); setEditingDate(true) }}
                  title="Click to set the shoot date"
                >
                  {formatShootDate(p.shootDate) || 'Set date'}
                </span>
              )}
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Visibility</span>
              <span className={styles.metaValue}>
                {isPrivate ? 'Private — only you' : 'Shared with the team'}
              </span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Created by</span>
              <span className={styles.metaValue}>{p.username} · {formatRelative(p.createdAt)}</span>
            </div>
          </div>

          {downloadHint && (
            <div className={styles.downloadPanel}>
              <div className={styles.downloadHead}>
                Saved <code>{downloadHint.file}</code> — {downloadHint.count} files, {downloadHint.gb} GB
              </div>
              <p>In the folder you want the media in, run:</p>
              <pre className={styles.downloadCmd}>curl --parallel --parallel-max 4 -C - --create-dirs -K ~/Downloads/{downloadHint.file}</pre>
              <p className={styles.downloadNote}>
                Downloads four at a time and recreates the date/device folders. <code>-C -</code> resumes,
                so if it's interrupted just run it again — it picks up where it stopped rather than
                starting over. Links are good for 12 hours.
              </p>
              <button className={styles.iconBtn} onClick={() => setDownloadHint(null)}>Dismiss</button>
            </div>
          )}

          <div className={styles.checkoutPanel}>
            {!isCheckedOut && (
              <>
                <div className={styles.checkoutStatus}>
                  <LockOpenIcon fontSize="small" style={{ color: '#6ee7b7' }} />
                  <span>Available — no one is editing</span>
                </div>
                <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => handleCheckOut(selectedId)}>
                  <LockIcon fontSize="small" /> Check out
                </button>
              </>
            )}
            {isCheckedOut && byMe && (
              <>
                <div className={styles.checkoutStatus}>
                  <LockIcon fontSize="small" style={{ color: '#fbbf24' }} />
                  <span>You have this checked out — {formatRelative(p.checkedOutAt)}</span>
                </div>
                <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => handleCheckIn(selectedId)}>
                  <LockOpenIcon fontSize="small" /> Check in
                </button>
              </>
            )}
            {isCheckedOut && !byMe && (
              <>
                <div className={styles.checkoutStatus}>
                  <LockIcon fontSize="small" style={{ color: '#f87171' }} />
                  <span><strong>{p.checkedOutBy}</strong> is editing — {formatRelative(p.checkedOutAt)}</span>
                </div>
                <button
                  className={`${styles.iconBtn} ${styles.warningBtn}`}
                  onClick={() => {
                    if (window.confirm(
                      `${p.checkedOutBy} has this checked out. Take it over?\n\n` +
                      'Editing the same raws in two catalogues produces conflicting .xmp ' +
                      'sidecars, and whoever pushes last wins.'
                    )) handleCheckOut(selectedId)
                  }}>
                  <LockIcon fontSize="small" /> Take over
                </button>
              </>
            )}
          </div>

          {detailLoading && assets.length === 0 && <div className={styles.status}>Loading…</div>}

          {!detailLoading && assets.length === 0 && (
            <div className={styles.emptyState}>
              <p>Nothing grouped under this shoot yet.</p>
              <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                Originals live in the library and stay there — a shoot just points at them,
                so the same file can belong to several shoots at once.
              </p>
              <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => setMode('picking')}>
                <AddIcon fontSize="small" /> Add from Library
              </button>
            </div>
          )}

          <div className={styles.photoGrid}>
            {items.map((asset, i) => {
              const isVideo = isVideoName(asset.filename)
              return (
                <div key={asset.path} className={styles.photoCard} onClick={() => setPreviewIdx(i)}>
                  <div className={styles.photoThumb}>
                    {asset.thumbnailUrl ? (
                      // onLoad clears the dimming a previous failure left behind: without it a
                      // tile that once timed out stays at 20% opacity even after the thumbnail
                      // becomes available, which reads as a much darker photo.
                      <img
                        src={storeUrl(asset.thumbnailUrl)}
                        alt={asset.filename}
                        loading="lazy"
                        onLoad={(e) => { e.currentTarget.style.opacity = '' }}
                        onError={(e) => { e.currentTarget.style.opacity = 0.2 }}
                      />
                    ) : (
                      <div className={styles.photoThumbPlaceholder}>
                        {isVideo ? <PlayCircleIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
                        <span>{isVideo ? 'video' : 'no preview'}</span>
                      </div>
                    )}
                    {isVideo && asset.thumbnailUrl && (
                      <div className={styles.playOverlay}><PlayCircleIcon /></div>
                    )}
                  </div>
                  <div className={styles.photoMeta}>
                    <div className={styles.photoName} title={asset.filename}>{asset.filename}</div>
                    <div className={styles.photoStats}>
                      <span>{formatBytes(asset.size)}</span>
                      <span>{asset.device || asset.date}</span>
                    </div>
                    {asset.sidecars.length > 0 && (
                      <div className={styles.sidecarRow}>
                        {asset.sidecars.map((s) => (
                          <a
                            key={s.path}
                            className={styles.sidecarChip}
                            href={storeUrl(s.downloadUrl)}
                            download={s.filename}
                            onClick={(e) => e.stopPropagation()}
                            title={`Download ${s.filename}`}
                          >
                            {extOf(s.filename).replace('.', '').toUpperCase() || 'FILE'}
                          </a>
                        ))}
                      </div>
                    )}
                    {(pathTaskMap[asset.path] || []).length > 0 && (
                      <div className={styles.taskLinkRow}>
                        {pathTaskMap[asset.path].map((t) => (
                          <span key={t.id} className={styles.taskLinkChip} title={`Produced for: ${t.title}`}>▸ {t.title}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {previewIdx !== null && items[previewIdx] && (
            <div className={styles.lightbox} onClick={() => setPreviewIdx(null)}>
              <button className={styles.lightboxClose} onClick={() => setPreviewIdx(null)} title="Close (Esc)">
                <CloseIcon />
              </button>
              {previewIdx > 0 && (
                <button
                  className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
                  onClick={(e) => { e.stopPropagation(); setPreviewIdx(previewIdx - 1) }}
                  title="Previous (←)">
                  <NavigateBeforeIcon />
                </button>
              )}
              <div className={styles.lightboxInner} onClick={(e) => e.stopPropagation()}>
                {isVideoName(items[previewIdx].filename) ? (
                  <video
                    key={items[previewIdx].path}
                    className={styles.lightboxVideo}
                    src={storeUrl(items[previewIdx].fileUrl)}
                    poster={items[previewIdx].thumbnailUrl ? storeUrl(items[previewIdx].thumbnailUrl) : undefined}
                    controls
                    autoPlay
                    preload="metadata"
                  />
                ) : items[previewIdx].thumbnailUrl ? (
                  <img src={storeUrl(items[previewIdx].thumbnailUrl)} alt={items[previewIdx].filename} />
                ) : (
                  <div className={styles.lightboxNoPreview}>No preview for this file type</div>
                )}
                <div className={styles.lightboxCaption}>
                  <span className={styles.lightboxCaptionMeta}>
                    <b>{items[previewIdx].filename}</b> · {formatBytes(items[previewIdx].size)} · {previewIdx + 1} / {items.length}
                  </span>
                  <span className={styles.lightboxActions} onClick={(e) => e.stopPropagation()}>
                    <a
                      className={styles.lightboxBtnPrimary}
                      href={storeUrl(items[previewIdx].downloadUrl)}
                      download={items[previewIdx].filename}
                    >
                      <DownloadIcon style={{ fontSize: '0.95rem' }} /> Download
                    </a>
                    <SidecarVersions sidecars={items[previewIdx].sidecars} storeUrl={storeUrl} styles={styles} />
                    <button
                      className={styles.lightboxBtn}
                      onClick={() => handleRemoveAsset(items[previewIdx])}
                      title="Remove from this shoot (the file stays in the library)"
                    >✕ remove</button>
                  </span>
                </div>

                {/* Link this file to the task(s) it was produced for. */}
                {(() => {
                  const cur = items[previewIdx]
                  const linked = pathTaskMap[cur.path] || []
                  const linkedIds = new Set(linked.map((t) => t.id))
                  const fileKind = isVideoName(cur.filename) ? 'Video' : 'Photography'
                  const q = taskQuery.trim().toLowerCase()
                  const picks = [...projectTasks]
                    .filter((t) => !q || t.properties.title.toLowerCase().includes(q))
                    .sort((a, b) => (a.properties.type === fileKind ? 0 : 1) - (b.properties.type === fileKind ? 0 : 1))
                  return (
                    <div className={styles.lightboxTaskRow} onClick={(e) => e.stopPropagation()}>
                      {linked.map((t) => (
                        <span key={t.id} className={styles.taskLinkChip}>
                          ▸ {t.title}
                          <button className={styles.taskLinkRemove} title="Unlink this task" onClick={() => handleUnlinkTask(cur, t.id)}>✕</button>
                        </span>
                      ))}
                      <div className={styles.taskPickerWrap}>
                        <button
                          className={styles.taskLinkAddBtn}
                          onClick={() => { setTaskPickerOpen((o) => !o); setTaskQuery('') }}
                        >＋ Link to task</button>
                        {taskPickerOpen && (
                          <div className={styles.taskPicker}>
                            <input
                              className={styles.taskPickerSearch}
                              placeholder="Search tasks…"
                              value={taskQuery}
                              onChange={(e) => setTaskQuery(e.target.value)}
                              autoFocus
                            />
                            <div className={styles.taskPickerHint}>{fileKind} tasks first · {picks.length} shown</div>
                            <div className={styles.taskPickerList}>
                              {picks.length === 0 && <div className={styles.taskPickerEmpty}>No matching tasks</div>}
                              {picks.map((t) => {
                                const already = linkedIds.has(t.identity)
                                return (
                                  <button
                                    key={t.identity}
                                    className={`${styles.taskPickerItem} ${already ? styles.taskPickerItemLinked : ''}`}
                                    disabled={already}
                                    onClick={() => { handleLinkToTask(cur, t.identity); setTaskPickerOpen(false); setTaskQuery('') }}
                                  >
                                    <span className={`${styles.taskPickerType} ${t.properties.type === fileKind ? styles.taskPickerTypeMatch : ''}`}>{t.properties.type}</span>
                                    <span className={styles.taskPickerTitle}>{t.properties.title}</span>
                                    {already && <span className={styles.taskPickerCheck}>✓ linked</span>}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      {linkMsg && <span className={styles.linkMsg}>{linkMsg}</span>}
                    </div>
                  )
                })()}
              </div>
              {previewIdx < items.length - 1 && (
                <button
                  className={`${styles.lightboxNav} ${styles.lightboxNext}`}
                  onClick={(e) => { e.stopPropagation(); setPreviewIdx(previewIdx + 1) }}
                  title="Next (→)">
                  <NavigateNextIcon />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ───────────────────── LIST VIEW ─────────────────────
  return (
    <div className={styles.view}>
      <div className={styles.gridBg} />
      <MediaCorner onClose={() => setIsVisible(false)} />

      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Media</div>
            <h1 className={styles.title}>Shoots</h1>
          </div>
          <div className={styles.toolbar}>
            <button className={styles.iconBtn} onClick={() => setMode('library')} title="Browse originals">
              <PhotoLibraryIcon fontSize="small" /> Library
            </button>
            <button className={styles.iconBtn} onClick={() => setMode('deliveries')} title="Finished exports handed to clients">
              <LocalShippingIcon fontSize="small" /> Deliveries
            </button>
            <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => setIsCreating(true)}>
              <AddIcon fontSize="small" /> New Shoot
            </button>
            <button className={styles.iconBtn} onClick={() => refetchList()} title="Reload">
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </header>

        {isCreating && (
          <form className={styles.createForm} onSubmit={handleCreate}>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Project</label>
              {/* Existing projects only — a typed field with a datalist merely
                  suggested, so a typo created a near-duplicate project. */}
              <select
                className={styles.select}
                value={newProject}
                onChange={e => setNewProject(e.target.value)}
                autoFocus
              >
                <option value="">
                  {existingProjects.length ? 'Choose a project…' : 'No projects yet — create one in Work first'}
                </option>
                {existingProjects.map(pr => (
                  <option key={pr.identity} value={pr.properties.title}>{pr.properties.title}</option>
                ))}
              </select>
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Shoot name</label>
              <input
                type="text"
                className={styles.input}
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Audi A3"
              />
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Shoot date</label>
              <input
                type="date"
                className={styles.input}
                value={newShootDate}
                onChange={e => setNewShootDate(e.target.value)}
              />
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Notes (optional)</label>
              <textarea
                className={styles.input}
                value={newNotes}
                onChange={e => setNewNotes(e.target.value)}
                placeholder="Client, location, car spec, anything worth remembering"
                rows={2}
              />
            </div>
            <div className={styles.createField}>
              <label className={styles.privateToggle}>
                <input type="checkbox" checked={newPrivate} onChange={e => setNewPrivate(e.target.checked)} />
                <span>Private — visible only to me (for personal or family shoots)</span>
              </label>
            </div>
            {createError && <div className={styles.errorMsg}>{createError}</div>}
            <div className={styles.createActions}>
              <button type="button" className={styles.iconBtn} onClick={() => { setIsCreating(false); setNewName(''); setNewProject(''); setNewNotes(''); setCreateError(null) }}>
                Cancel
              </button>
              <button type="submit" className={`${styles.iconBtn} ${styles.primaryBtn}`} disabled={creating || !newName.trim() || !newProject}>
                {creating ? 'Creating…' : 'Create shoot'}
              </button>
            </div>
          </form>
        )}

        {listLoading && shoots.length === 0 && <div className={styles.status}>Loading…</div>}
        {!listLoading && shoots.length === 0 && !isCreating && (
          <div className={styles.emptyState}>
            <p>No shoots yet.</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              A shoot groups originals from the library — it doesn’t hold files of its own.
            </p>
            <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => setIsCreating(true)}>
              <AddIcon fontSize="small" /> Create the first shoot
            </button>
          </div>
        )}

        {groupShootsByProject(shoots).map(group => (
          <div key={group.project} className={styles.projectGroup}>
            <div className={styles.projectGroupHeader}>{group.project}</div>
            <div className={styles.projectGrid}>
              {group.shoots.map(s => {
                const pp = s.properties
                const checkedOut = !!pp.checkedOutBy
                const byMe = pp.checkedOutBy === username
                return (
                  <div key={s.identity} className={styles.projectCard} onClick={() => handleSelect(s.identity)}>
                    <div className={styles.projectCardHeader}>
                      <div className={styles.projectName}>{pp.name}</div>
                      <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                        {pp.visibility === 'private' && (
                          <span className={styles.privateBadge} title="Private — only you can see this">
                            <VisibilityOffIcon style={{ fontSize: 12 }} /> private
                          </span>
                        )}
                        {checkedOut && (
                          <span className={`${styles.checkoutBadge} ${byMe ? styles.checkoutByMe : styles.checkoutByOther}`}>
                            <LockIcon style={{ fontSize: 12 }} />
                            {byMe ? 'you' : pp.checkedOutBy}
                          </span>
                        )}
                      </div>
                    </div>
                    {pp.notes && <div className={styles.projectNotes}>{pp.notes}</div>}
                    <div className={styles.projectMeta}>
                      <span className={styles.slugTag}>{pp.assetCount} original{pp.assetCount === 1 ? '' : 's'}</span>
                      <span>{formatShootDate(pp.shootDate) || formatRelative(pp.createdAt)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
